import React, { useState, Children } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2Icon, PlusIcon, MinusIcon, ShoppingBagIcon, ArrowRightIcon, ShieldCheckIcon, HeartIcon, ChevronDownIcon, ChevronUpIcon, SearchIcon, XIcon } from 'lucide-react';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
// Mock cart data
const initialCartItems = [{
  id: 1,
  title: 'The Silent Echo',
  author: 'Elena Richards',
  format: 'Hardcover',
  price: 24.99,
  quantity: 1,
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=800'
}, {
  id: 2,
  title: 'Heart of the Storm',
  author: 'Marcus Chen',
  format: 'Paperback',
  price: 16.99,
  quantity: 1,
  coverImage: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=800'
}, {
  id: 3,
  title: 'Bookish & Spice Box',
  author: 'Subscription',
  format: 'Monthly Box',
  price: 39.99,
  quantity: 1,
  coverImage: 'https://images.unsplash.com/photo-1603162361777-1f802e442ecf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=800'
}];
// Mock nonprofits for donation selection
const availableNonprofits = [{
  id: '1',
  name: "Children's Literacy Foundation",
  category: 'Education'
}, {
  id: '2',
  name: 'Book Access for All',
  category: 'Education'
}, {
  id: '3',
  name: 'Libraries First',
  category: 'Education'
}, {
  id: '4',
  name: 'Rural Library Project',
  category: 'Education'
}];
// Mock seller donation preferences
const sellerDonationPrefs = {
  'Elena Richards': {
    nonprofitId: '1',
    nonprofitName: "Children's Literacy Foundation",
    percentage: 5
  },
  'Marcus Chen': null
};
const Cart = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState(initialCartItems);
  const [promoCode, setPromoCode] = useState('');
  // Donation state
  const [showDonationSection, setShowDonationSection] = useState(false);
  const [donationType, setDonationType] = useState<'reader' | 'seller'>('reader');
  const [selectedNonprofit, setSelectedNonprofit] = useState<string | null>(null);
  const [donationAmount, setDonationAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [nonprofitSearch, setNonprofitSearch] = useState('');
  const updateQuantity = (id: number, change: number) => {
    setCartItems(prev => prev.map(item => {
      if (item.id === id) {
        const newQuantity = Math.max(1, item.quantity + change);
        return {
          ...item,
          quantity: newQuantity
        };
      }
      return item;
    }));
  };
  const removeItem = (id: number) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  };
  // Calculate seller donations from cart items
  const sellerDonations = cartItems.reduce((acc, item) => {
    const sellerPref = sellerDonationPrefs[item.author as keyof typeof sellerDonationPrefs];
    if (sellerPref) {
      const donationAmt = item.price * item.quantity * sellerPref.percentage / 100;
      if (!acc[sellerPref.nonprofitName]) {
        acc[sellerPref.nonprofitName] = 0;
      }
      acc[sellerPref.nonprofitName] += donationAmt;
    }
    return acc;
  }, {} as Record<string, number>);
  const totalSellerDonations = Object.values(sellerDonations).reduce((sum, amt) => sum + amt, 0);
  const readerDonation = donationAmount || (customAmount ? parseFloat(customAmount) : 0) || 0;
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = 5.99;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax + readerDonation;
  if (cartItems.length === 0) {
    return <div className={`min-h-screen py-12 px-4 sm:px-6 lg:px-8 ${dk.page}`} data-id="element-8765" data-mp="32z7yywdustlc">
        <div className="max-w-3xl mx-auto text-center" data-id="element-8766" data-mp="2xzlm4o1xtfd2">
          <div className="flex justify-center mb-6" data-id="element-8767" data-mp="3bxjvtg5bcnm8">
            <div className={`p-6 rounded-full shadow-md ${dk.card}`} data-id="element-8768" data-mp="3bz3flgq6hgsr">
              <ShoppingBagIcon className={`h-12 w-12 ${dk.textMuted}`} data-id="element-8769" data-mp="36xnszk8cr289" />
            </div>
          </div>
          <h2 className={`text-3xl font-bold mb-4 ${dk.textHeading}`} data-id="element-8770" data-mp="3luoo7xpr5fbh">
            Your cart is empty
          </h2>
          <p className={`text-lg mb-8 ${dk.textSecondary}`} data-id="element-8771" data-mp="3gwdb9e45uop1">
            Looks like you haven't added any books to your cart yet.
          </p>
          <Link to="/browse" className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm ${rt.primaryButton}`} data-id="element-8772" data-mp="3qmyb14vklz9j">
            Start Browsing
          </Link>
        </div>
      </div>;
  }
  return <div className={`min-h-screen py-12 px-4 sm:px-6 lg:px-8 ${dk.page}`} data-id="element-8773" data-mp="n3sckixn8gsx">
      <div className="max-w-7xl mx-auto" data-id="element-8774" data-mp="n1pct76ov9b0">
        <h1 className={`text-3xl font-bold mb-8 ${dk.textHeading}`} data-id="element-8775" data-mp="s3eq6thdvz29">
          Shopping Cart ({cartItems.length} items)
        </h1>

        <div className="lg:grid lg:grid-cols-12 lg:gap-x-12 lg:items-start" data-id="element-8776" data-mp="d52uap1u4uc5">
          {/* Cart Items */}
          <section className="lg:col-span-7" data-id="element-8777" data-mp="i34exeewt1pq">
            <ul className={`divide-y border-t border-b ${dk.divider}`} data-id="element-8778" data-mp="4dke4e4w2ej2">
              {cartItems.map(item => <li key={item.id} className="flex py-6 sm:py-10" data-id="element-8779" data-mp="9494fq4u15gm">
                  <div className="flex-shrink-0" data-id="element-8780" data-mp="3qkiotfjtjq32">
                    <img src={item.coverImage} alt={item.title} className="w-24 h-32 sm:w-32 sm:h-48 rounded-md object-cover object-center shadow-sm" data-id="element-8781" data-mp="3qltpv7lvqjqh" />
                  </div>

                  <div className="ml-4 flex-1 flex flex-col justify-between sm:ml-6" data-id="element-8782" data-mp="3ll61yusy6j0h">
                    <div className="relative pr-9 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:pr-0" data-id="element-8783" data-mp="1s2oz2yl5uwr6">
                      <div data-id="element-8784" data-mp="1nbytplcqitwh">
                        <div className="flex justify-between" data-id="element-8785" data-mp="1nbp1dr47wanm">
                          <h3 className={`text-lg font-medium ${dk.textPrimary}`} data-id="element-8786" data-mp="1na5jxx6trtk9">
                            <Link to={`/book/${item.id}`} data-id="element-8787" data-mp="1dekf7radgf6t">{item.title}</Link>
                          </h3>
                        </div>
                        <p className={`mt-1 text-sm ${dk.textSecondary}`} data-id="element-8788" data-mp="2b0rsrqonlhvb">
                          {item.author}
                        </p>
                        <p className={`mt-1 text-sm font-medium ${rt.textPrimary}`} data-id="element-8789" data-mp="2133mc3hoqlxb">
                          {item.format}
                        </p>
                        <p className={`mt-1 text-lg font-medium ${dk.textPrimary}`} data-id="element-8790" data-mp="poq1z8m54om1">
                          ${item.price.toFixed(2)}
                        </p>
                      </div>

                      <div className="mt-4 sm:mt-0 sm:pr-9" data-id="element-8791" data-mp="2xbjftb2jqz0k">
                        <div className={`flex items-center border rounded-md w-32 ${dk.border}`} data-id="element-8792" data-mp="31b1w1tjfalx2">
                          <button onClick={() => updateQuantity(item.id, -1)} className={`p-2 ${dk.textMuted} hover:${dk.textPrimary}`} data-id="element-8793" data-mp="368jyr26uk4wm">
                            <MinusIcon className="h-4 w-4" data-id="element-8794" data-mp="3kzj506k7mhd1" />
                          </button>
                          <span className={`flex-1 text-center ${dk.textPrimary}`} data-id="element-8795" data-mp="3l1dnle80dxdy">
                            {item.quantity}
                          </span>
                          <button onClick={() => updateQuantity(item.id, 1)} className={`p-2 ${dk.textMuted} hover:${dk.textPrimary}`} data-id="element-8796" data-mp="1und7gq6lc7qq">
                            <PlusIcon className="h-4 w-4" data-id="element-8797" data-mp="5dkls53fc91y" />
                          </button>
                        </div>

                        <div className="absolute top-0 right-0" data-id="element-8798" data-mp="1c96ase9l47h">
                          <button type="button" onClick={() => removeItem(item.id)} className={`-m-2 p-2 inline-flex ${dk.textMuted} hover:${dk.textPrimary}`} data-id="element-8799" data-mp="f4p49esb6c1j">
                            <span className="sr-only" data-id="element-8800" data-mp="3ejakj60kcclv">Remove</span>
                            <Trash2Icon className="h-5 w-5" aria-hidden="true" data-id="element-8801" data-mp="3ekvil5m36doh" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>)}
            </ul>

            {/* Donation Section */}
            <div className={`mt-8 rounded-lg border overflow-hidden ${dk.card}`} data-id="element-8802" data-mp="12t87hfwi3ymr">
              <button onClick={() => setShowDonationSection(!showDonationSection)} className={`w-full flex items-center justify-between p-4 transition-colors ${dk.dropdownItemHover}`} data-id="element-8803" data-mp="2kq1ygwz0gl9t">
                <div className="flex items-center" data-id="element-8804" data-mp="2gphy7kj551jg">
                  <div className={`p-2 rounded-full mr-3 ${dk.isDark ? 'bg-rose-900/30 text-rose-400' : 'bg-rose-100 text-rose-500'}`} data-id="element-8805" data-mp="2gqb7as5ko34r">
                    <HeartIcon className="h-5 w-5" data-id="element-8806" data-mp="38fw5uz0k9hmo" />
                  </div>
                  <div className="text-left" data-id="element-8807" data-mp="2uq2ikl4pf5f2">
                    <h3 className={`font-medium ${dk.textPrimary}`} data-id="element-8808" data-mp="2up13m5kkhys2">
                      Add a Donation
                    </h3>
                    <p className={`text-sm ${dk.textSecondary}`} data-id="element-8809" data-mp="3ne843a3l0yfy">
                      Support a cause with your purchase
                    </p>
                  </div>
                </div>
                {showDonationSection ? <ChevronUpIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-8810" data-mp="3h6pcboxgwzd6" /> : <ChevronDownIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-8811" data-mp="3m6borw5h2bkx" />}
              </button>

              {showDonationSection && <div className={`p-4 border-t ${dk.divider}`} data-id="element-8812" data-mp="388df3423j3br">
                  {/* Seller Donations Info */}
                  {totalSellerDonations > 0 && <div className={`mb-4 p-3 rounded-lg ${dk.isDark ? 'bg-indigo-900/20 border border-indigo-800/30' : 'bg-indigo-50 border border-indigo-100'}`} data-id="element-8813" data-mp="2yfpgc3rk2pae">
                      <p className={`text-sm font-medium mb-2 ${dk.isDark ? 'text-indigo-300' : 'text-indigo-700'}`} data-id="element-8814" data-mp="2ojw64r1b77vs">
                        Seller Contributions
                      </p>
                      {Object.entries(sellerDonations).map(([nonprofit, amount]) => <div key={nonprofit} className="flex items-center justify-between text-sm" data-id="element-8815" data-mp="20uo6icunow6u">
                            <span className={dk.isDark ? 'text-indigo-200' : 'text-indigo-600'} data-id="element-8816" data-mp="29vme154azlu6">
                              {nonprofit}
                            </span>
                            <span className={`font-medium ${dk.isDark ? 'text-indigo-200' : 'text-indigo-700'}`} data-id="element-8817" data-mp="2eu5zz5iwcxmj">
                              ${amount.toFixed(2)}
                            </span>
                          </div>)}
                      <p className={`text-xs mt-2 ${dk.isDark ? 'text-indigo-400' : 'text-indigo-500'}`} data-id="element-8818" data-mp="2xl70vz3kv38q">
                        These donations are automatically included by the
                        sellers
                      </p>
                    </div>}

                  {/* Donation Type Toggle */}
                  <div className="mb-4" data-id="element-8819" data-mp="246co8vteqhv6">
                    <p className={`text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-8820" data-mp="28zpch4u6cmne">
                      Choose how to donate
                    </p>
                    <div className="grid grid-cols-2 gap-2" data-id="element-8821" data-mp="2iwtz15rxmk4q">
                      <button onClick={() => setDonationType('reader')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${donationType === 'reader' ? `${rt.bgMedium} ${rt.textPrimary} ring-2 ring-indigo-500` : `${dk.surface} ${dk.textSecondary} hover:${dk.surfaceMuted}`}`} data-id="element-8822" data-mp="2izg4mzv9ipkb">
                        My Choice
                      </button>
                      <button onClick={() => {
                    setDonationType('seller');
                    const firstSellerPref = Object.values(sellerDonationPrefs).find(p => p !== null);
                    if (firstSellerPref) {
                      setSelectedNonprofit(firstSellerPref.nonprofitId);
                    }
                  }} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${donationType === 'seller' ? `${rt.bgMedium} ${rt.textPrimary} ring-2 ring-indigo-500` : `${dk.surface} ${dk.textSecondary} hover:${dk.surfaceMuted}`}`} data-id="element-8823" data-mp="q07xt2euqyiz">
                        Seller's Choice
                      </button>
                    </div>
                  </div>

                  {/* Seller's Choice Info */}
                  {donationType === 'seller' && <div className={`mb-4 p-3 rounded-lg ${dk.surfaceMuted}`} data-id="element-8824" data-mp="3frwcifc36z3v">
                      <p className={`text-sm ${dk.textSecondary}`} data-id="element-8825" data-mp="17fk90shl3do4">
                        <span className="font-medium" data-id="element-8826" data-mp="1c83j3fh2bqo6">Elena Richards</span>{' '}
                        supports{' '}
                        <span className="font-medium" data-id="element-8827" data-mp="yhqbxfbzwfzw">
                          Children's Literacy Foundation
                        </span>
                      </p>
                    </div>}

                  {/* Reader's Choice - Nonprofit Search */}
                  {donationType === 'reader' && <div className="mb-4" data-id="element-8828" data-mp="1v4lkyppml6y5">
                      <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-8829" data-mp="1h7g8a6vep9tx">
                        Select a nonprofit
                      </label>
                      <div className="relative mb-2" data-id="element-8830" data-mp="5qq7j39r5a7p">
                        <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dk.textMuted}`} data-id="element-8831" data-mp="5u49iaei7hem" />
                        <input type="text" value={nonprofitSearch} onChange={e => setNonprofitSearch(e.target.value)} placeholder="Search nonprofits..." className={`w-full pl-10 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${dk.input}`} data-id="element-8832" data-mp="2898k6lutmgi" />
                      </div>
                      <div className="space-y-1 max-h-40 overflow-y-auto" data-id="element-8833" data-mp="3aq5mvrnkxvcu">
                        {availableNonprofits.filter(np => np.name.toLowerCase().includes(nonprofitSearch.toLowerCase())).map(nonprofit => <button key={nonprofit.id} onClick={() => setSelectedNonprofit(nonprofit.id)} className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors ${selectedNonprofit === nonprofit.id ? `${rt.bgMedium} ${rt.textPrimary}` : `${dk.dropdownItemHover} ${dk.textSecondary}`}`} data-id="element-8834" data-mp="2rznyu0doznxt">
                              <span className="text-sm font-medium" data-id="element-8835" data-mp="19mmo6w6p095u">
                                {nonprofit.name}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${dk.badgeNeutral}`} data-id="element-8836" data-mp="hvqbpwojo6j3">
                                {nonprofit.category}
                              </span>
                            </button>)}
                      </div>
                    </div>}

                  {/* Donation Amount */}
                  <div data-id="element-8837" data-mp="22asd7v4s4kbh">
                    <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-8838" data-mp="22e6bar6eu7d4">
                      Donation amount
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2" data-id="element-8839" data-mp="27j0bl4gl9w4y">
                      {[1, 2, 5, 10].map(amount => <button key={amount} onClick={() => {
                    setDonationAmount(amount);
                    setCustomAmount('');
                  }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${donationAmount === amount ? `${rt.bgMedium} ${rt.textPrimary} ring-2 ring-indigo-500` : `${dk.surface} ${dk.textSecondary} hover:${dk.surfaceMuted}`}`} data-id="element-8840" data-mp="1yovgg8uf9yjm">
                          ${amount}
                        </button>)}
                      <div className="relative flex-1 min-w-[100px]" data-id="element-8841" data-mp="3a393jbhcq9yl">
                        <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${dk.textMuted}`} data-id="element-8842" data-mp="3a1xtwv1867zk">
                          $
                        </span>
                        <input type="number" value={customAmount} onChange={e => {
                      setCustomAmount(e.target.value);
                      setDonationAmount(null);
                    }} placeholder="Other" className={`w-full pl-7 pr-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${dk.input}`} data-id="element-8843" data-mp="3jn8upa96ggoe" />
                      </div>
                    </div>
                    {readerDonation > 0 && selectedNonprofit && <p className={`text-sm ${dk.isDark ? 'text-rose-400' : 'text-rose-600'}`} data-id="element-8844" data-mp="yx9ph8jiolph">
                        ${readerDonation.toFixed(2)} will be donated to{' '}
                        {availableNonprofits.find(np => np.id === selectedNonprofit)?.name}
                      </p>}
                  </div>

                  {/* Clear Donation */}
                  {(readerDonation > 0 || selectedNonprofit) && <button onClick={() => {
                setDonationAmount(null);
                setCustomAmount('');
                setSelectedNonprofit(null);
              }} className={`mt-3 text-sm flex items-center ${dk.textMuted} hover:${dk.textPrimary}`} data-id="element-8845" data-mp="1wo6e96t07a3z">
                      <XIcon className="h-4 w-4 mr-1" data-id="element-8846" data-mp="bdprd4yp3c58" />
                      Clear donation
                    </button>}
                </div>}
            </div>
          </section>

          {/* Order Summary */}
          <section className={`lg:col-span-5 mt-16 lg:mt-0 rounded-lg px-4 py-6 sm:p-6 lg:p-8 shadow-md ${dk.card}`} data-id="element-8847" data-mp="3asfirih4lp4m">
            <h2 className={`text-lg font-medium ${dk.textHeading}`} data-id="element-8848" data-mp="3kklacr3gu4ja">
              Order Summary
            </h2>

            <dl className={`mt-6 space-y-4 ${dk.textSecondary}`} data-id="element-8849" data-mp="3fma1aikzvy24">
              <div className="flex items-center justify-between" data-id="element-8850" data-mp="2rvebfr03ncxi">
                <dt className="text-sm" data-id="element-8851" data-mp="2rxqt031y3la1">Subtotal</dt>
                <dd className="text-sm font-medium" data-id="element-8852" data-mp="2mxcx1jdmc1sd">${subtotal.toFixed(2)}</dd>
              </div>
              <div className={`flex items-center justify-between border-t pt-4 ${dk.divider}`} data-id="element-8853" data-mp="30wkvub5vd7w1">
                <dt className="flex items-center text-sm" data-id="element-8854" data-mp="2wt65aqkb194c">
                  <span data-id="element-8855" data-mp="g5ke00jjoida">Shipping estimate</span>
                </dt>
                <dd className="text-sm font-medium" data-id="element-8856" data-mp="g7yamdtvi2my">${shipping.toFixed(2)}</dd>
              </div>
              <div className={`flex items-center justify-between border-t pt-4 ${dk.divider}`} data-id="element-8857" data-mp="c6usfkocruik">
                <dt className="flex text-sm" data-id="element-8858" data-mp="onnh8h5amc15">
                  <span data-id="element-8859" data-mp="oofd7t2sybry">Tax estimate</span>
                </dt>
                <dd className="text-sm font-medium" data-id="element-8860" data-mp="9qn0t9sjy3hz">${tax.toFixed(2)}</dd>
              </div>

              {/* Reader Donation Line */}
              {readerDonation > 0 && <div className={`flex items-center justify-between border-t pt-4 ${dk.divider}`} data-id="element-8861" data-mp="12esl5vfp1qha">
                  <dt className={`flex items-center text-sm ${dk.isDark ? 'text-rose-400' : 'text-rose-600'}`} data-id="element-8862" data-mp="17ckemtvzlknl">
                    <HeartIcon className="h-4 w-4 mr-1" data-id="element-8863" data-mp="xnso8q6kr8es" />
                    <span data-id="element-8864" data-mp="xm7tp0k9fpgx">Your donation</span>
                  </dt>
                  <dd className={`text-sm font-medium ${dk.isDark ? 'text-rose-400' : 'text-rose-600'}`} data-id="element-8865" data-mp="1lcv721cy37av">
                    ${readerDonation.toFixed(2)}
                  </dd>
                </div>}

              {/* Seller Donations Line */}
              {totalSellerDonations > 0 && <div className={`flex items-center justify-between ${readerDonation === 0 ? 'border-t pt-4' : ''} ${dk.divider}`} data-id="element-8866" data-mp="1v1s8erfyl24m">
                  <dt className={`flex items-center text-sm ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-8867" data-mp="1lz9a8kn4bdop">
                    <HeartIcon className="h-4 w-4 mr-1" data-id="element-8868" data-mp="1cahm6nl6hdj2" />
                    <span data-id="element-8869" data-mp="1c9ps5h78bnuw">Seller donations</span>
                  </dt>
                  <dd className={`text-sm font-medium ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-8870" data-mp="17amvol5bepyj">
                    ${totalSellerDonations.toFixed(2)}
                  </dd>
                </div>}

              <div className={`flex items-center justify-between border-t pt-4 ${dk.divider}`} data-id="element-8871" data-mp="yckmhibfgr06">
                <dt className={`text-base font-medium ${dk.textHeading}`} data-id="element-8872" data-mp="34zibj8e27p79">
                  Order total
                </dt>
                <dd className={`text-base font-medium ${dk.textHeading}`} data-id="element-8873" data-mp="39ws6t4o4qv30">
                  ${total.toFixed(2)}
                </dd>
              </div>
            </dl>

            <div className="mt-6" data-id="element-8874" data-mp="3iz9vdpr9bzt6">
              <div className="flex space-x-2" data-id="element-8875" data-mp="3iy86oihbjesz">
                <input type="text" value={promoCode} onChange={e => setPromoCode(e.target.value)} placeholder="Promo code" className={`block w-full shadow-sm sm:text-sm rounded-md ${dk.input}`} data-id="element-8876" data-mp="2m8ha8dpgvhkj" />
                <button type="button" className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${rt.secondaryButton}`} data-id="element-8877" data-mp="2zznk93qdgxlo">
                  Apply
                </button>
              </div>
            </div>

            <div className="mt-6" data-id="element-8878" data-mp="27hials6m03d3">
              <button type="button" onClick={() => navigate('/checkout')} className={`w-full border border-transparent rounded-md shadow-sm py-3 px-4 text-base font-medium flex items-center justify-center ${rt.primaryButton}`} data-id="element-8879" data-mp="1oegfbciyfncq">
                Checkout
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-8880" data-mp="1td1f5auao50n" />
              </button>
            </div>

            <div className={`mt-6 flex justify-center items-center space-x-2 text-sm ${dk.textMuted}`} data-id="element-8881" data-mp="274fzbkzxuc9a">
              <ShieldCheckIcon className="h-4 w-4" data-id="element-8882" data-mp="2264rfft7ebtp" />
              <span data-id="element-8883" data-mp="2h3x31wvmqg2m">Secure Checkout</span>
            </div>
          </section>
        </div>
      </div>
    </div>;
};
export default Cart;